import React from 'react';
import { Navigate } from 'react-router-dom';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { useRole } from '@/hooks/use-role';

interface ProductionAccessGuardProps {
  children: React.ReactNode;
}

/**
 * BLU-658: URL-typing defense for pure-client production isolation.
 *
 * AppSidebar already hides the workspace toggle and auto-pins a floor-less
 * org to the inventory workspace, but that doesn't stop a direct navigation
 * to a /production/* URL. This wraps the /production/* route block so that
 * path redirects to /orders too.
 *
 * BLU-670: gates on `hidesProduction` (owns no non-delegated location) — the
 * junction-agnostic twin of the backend `requireOwnedLocation` middleware —
 * NOT on `isPureClient`, so the nav and the API agree for every org shape
 * (a brand-new zero-location org is not a pure client but still has no
 * Production floor).
 *
 * `hidesProduction` is false while the org-role-surfaces query is
 * loading/disabled/erroring (see useOrgRoleSurfaces), so this never redirects
 * on an unconfirmed answer — an owner org never bounces off a loading blip.
 *
 * BLU-367 CX access: the CX role is deliberately designed to work for a pure
 * client org (e.g. customer-support staff at a 3PL client watching orders
 * fulfilled at a shared floor it doesn't own) — its entire nav is Tracker +
 * Issues, both already junction-aware on the backend
 * (productionCoordinatorOrWarehouseStaffReadProcedure /
 * productionCoordinatorOrWarehouseStaffProcedure use
 * requireOwnedLocationOrClientJunction, not requireOwnedLocation). Exempt CX
 * outright rather than path-matching — unlike the legacy
 * CLIENT_PRODUCTION_PATH single-path exemption in AppSidebar.tsx, CX has no
 * route in its nav that would 403 anyway.
 */
export default function ProductionAccessGuard({ children }: ProductionAccessGuardProps) {
  const { hidesProduction } = useOrgRoleSurfaces();
  const { role } = useRole();

  if (hidesProduction && role !== 'CX') {
    return <Navigate to="/orders" replace />;
  }

  return <>{children}</>;
}
